import {Comment, PublicChannelSettings} from "../../api/Models";
import React, {FormEvent, useCallback, useEffect, useRef, useState} from "react";
import {useAuthenticatedApi, useLocalStorage} from "@webng/react-app-common";
import {ExternalUserProvider, useExternalUser} from "../../ExternalUserProvider";
import {CreateCommentNeedsLogin} from "./CreateCommentNeedsLogin";
import TextareaAutosize from 'react-textarea-autosize';
import {useTranslation} from "react-i18next";
import { Send } from "react-feather";
import { CircleLoader } from "../shared/MoreIcons";
import {ApiClient, isApiResponseError} from '@webng/tickaroo-api'


interface CreateCommentFormProps {
  ownerId: string
  channelId: string
  onSuccess(comment: Comment): void
  externalUserProvider?: ExternalUserProvider
  settings?: PublicChannelSettings
  useCookies?: boolean
}

interface FormState {
  name: string
  text: string
  submitPending: true|false
  status?: string
  error?: string
  validationErrors?: ValidationErrors
}

interface ValidationErrors {
  name?: string
  text?: string
}

interface ExternalUserNameProps {
  name: string
}

interface AnonymousAuthor {
  name: string
}

function ExternalUserName({name}: ExternalUserNameProps) {
  return <input type="hidden" value={name} name="name" disabled={true}/>
}

const MAX_COMMENT_LENGTH = 2000

export default function CreateCommentForm({ownerId, channelId, onSuccess, externalUserProvider, settings, useCookies}: CreateCommentFormProps){
  const apiClient = useAuthenticatedApi();
  const [anonymousAuthor, setAnonymousAuthor] = useLocalStorage<AnonymousAuthor>(useCookies ? "tickaroo-comments-author": null, {name: ""})
  const [formState, setFormState] = useState<FormState>({ name: anonymousAuthor.name, text: "", submitPending: false})
  const externalUser = useExternalUser(externalUserProvider);
  const {t} = useTranslation("liveblog");

  // const queryClient = useQueryClient();

  const validate = useCallback(() => {
    const errors:ValidationErrors = {};
    if(!externalUser && !apiClient.isAuthenticated() && (formState.name === "" || formState.name.length < 3)){
      errors.name = t('liveblog.comments.create.validation.name.too_short');
    }
    if(formState.text === "" || formState.text.length < 2){
      errors.text = t('liveblog.comments.create.validation.text.too_short');
    }
    return errors;
  }, [externalUser, apiClient, formState.name, formState.text, t])

  const handleSubmit = useCallback(async (event: FormEvent<HTMLFormElement> | null) => {
    event && event.preventDefault();
    const errors = validate();
    if(Object.keys(errors).length > 0){
      setFormState(prevState => ({...prevState, validationErrors: errors}));
    } else {
      setFormState(prevState => ({...prevState, submitPending: true, status: undefined, error: undefined, validationErrors: undefined}));
      const postData: Record<string, string|undefined> = {channelId: channelId, text: formState.text};
      if(externalUser && apiClient.isAuthenticated()) {
        postData['authorName'] = externalUser.name;
        if(externalUser.picture && externalUser.picture !== "") {
          postData['authorPicture'] = externalUser.picture;
        }
      } else if(externalUser && externalUser.idTokenCallback) {
        postData['idToken'] = await externalUser.idTokenCallback();
      } else {
        postData['authorName'] = formState.name;
        setAnonymousAuthor({name: formState.name});
      }
      try {
        const response = await apiClient.postJson(ApiClient.API_COMMENTS_V1_COMMENTS_CREATE, postData, {ownerId})
        let formStatus = "";
        if(response.publishedAt){
          formStatus = t('liveblog.comments.create.status.published')
          //TODO
          // queryClient.getQueryData([]) XX
        } else {
          formStatus = t('liveblog.comments.create.status.in_approval')
        }
        setFormState(prevState => ({name: prevState.name, text: "", submitPending: false, status: formStatus}));
        onSuccess(response);
      } catch(e) {
        if(isApiResponseError(e)){
          let errorMsg = t('liveblog.comments.create.generic_error');
          if(e.json){
            errorMsg = `Api Error (${e.status}): ${e.json.message ? e.json.message : e.json.errors.join(", ")}`;
          }
          setFormState(prevState => ({...prevState, submitPending: false, status: undefined, error: errorMsg}))
        } else {
          throw e;
        }
      }
    }
  }, [apiClient, channelId, externalUser, formState.name, formState.text, onSuccess, ownerId, setAnonymousAuthor, t, validate]);

  const keyHandler = useCallback((keyEvent: KeyboardEvent) => {
    const sender = keyEvent.target  as HTMLElement;
    if(sender) {
      const ctrlOrMeta = keyEvent.metaKey || keyEvent.ctrlKey;
      if(ctrlOrMeta) {
        switch (keyEvent.keyCode) {
          case 13: //enter
            handleSubmit(null)
            keyEvent.preventDefault()
            break
        }
      }
    }
  }, [handleSubmit])

  const wrapperRef = useRef<HTMLFormElement>(null)
  useEffect(() => {
    const ref = wrapperRef.current
    ref?.addEventListener('keydown', keyHandler)
    return () => ref?.removeEventListener('keydown', keyHandler);
  }, [keyHandler]);

  function handleFormChange(key: string, value: string){
    setFormState(prevState => ({...prevState, [key]: value}));
  }

  if(!settings?.allowAnonymousPost && !externalUser && !apiClient.isAuthenticated()) {
    return <CreateCommentNeedsLogin triggerLogin={externalUserProvider?.triggerLogin} />
  }

  let count_class_names = "";
  if (formState.text.length/MAX_COMMENT_LENGTH >= 0.5) { count_class_names += "tik4-create-comment__count--reached-half "; }
  if (formState.text.length/MAX_COMMENT_LENGTH >= 0.85) { count_class_names += "tik4-create-comment__count--reached-almost "; }
  if (formState.text.length/MAX_COMMENT_LENGTH >= 1) { count_class_names += "tik4-create-comment__count--reached-limit "; }

  return (<div className="tik4-create-comment">
    <form className="tik4-create-comment__form" onSubmit={handleSubmit} ref={wrapperRef}>
      {formState.error &&
        <div className="tik4-create-comment__error">
          {formState.error}
        </div>
      }

      {externalUser && <ExternalUserName name={externalUser.name} />}

      {!externalUser && <div className="tik4-create-comment__name-group">
        <div className="tik4-create-comment__name-form-group">
          <label className="tik4-create-comment__name-label" htmlFor="tik4-create-comment__name-field">{t('liveblog.comments.create.name.label')}</label>
          <input autoFocus={true} className="tik4-form-field tik4-create-comment__name-field" type="text" id="tik4-create-comment__name-field" name="name" value={formState.name} onChange={(event) => handleFormChange("name", event.target.value.normalize("NFC"))} placeholder=" "/>
          {/* Empty (space) placeholder is required to fix label alignment bug in safari */}
        </div>

        {formState.validationErrors && formState.validationErrors.name &&
          <div className="tik4-feedback tik4-create-comment__warning">
            <div className="tik4-feedback__text">
              {formState.validationErrors.name}
            </div>
          </div>
        }
      </div>}

      <div className="tik4-create-comment__comment-submit-group">
        <div className="tik4-create-comment__comment-submit">
          <TextareaAutosize
            autoFocus={externalUser && true}
            className="tik4-form-field tik4-create-comment__comment"
            rows={1} id="text" name="text" value={formState.text}
            onChange={(event) => handleFormChange("text", event.target.value.normalize("NFC").substr(0, MAX_COMMENT_LENGTH))}
            placeholder={externalUser ? t('liveblog.comments.button.comment.as', {username: externalUser.name}) : t('liveblog.comments.button.comment.generic')}
          />
          <button className="tik4-button tik4-button--primary tik4-create-comment__submit" title={t('liveblog.comments.create.submit.title')} disabled={formState.submitPending} type="submit">
            { formState.submitPending ?
                <CircleLoader className="tik4-icon tik4-spin" />
              :
                <Send className="tik4-icon" />
            }
          </button>
        </div>
        <div className={`tik4-create-comment__count ${count_class_names}`}>
          {t('liveblog.comments.create.text.character_count', {count: MAX_COMMENT_LENGTH - formState.text.length, max: MAX_COMMENT_LENGTH})}
        </div>
        {formState.validationErrors && formState.validationErrors.text &&
          <div className="tik4-feedback tik4-create-comment__warning">
            <div className="tik4-feedback__text">{formState.validationErrors.text}</div>
          </div>
        }
      </div>
      {formState.status &&
        <div className="tik4-create-comment__in-approval">
          {formState.status}
        </div>
      }
    </form>
  </div>)
}
